import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WorksheetService } from '../../services/worksheet.service';
import { WorksheetDto } from '../../models/worksheet-dto.interface';
import { JobWorksheetHeaderComponent } from '../job-details/worksheet/job-worksheet-header.component';

@Component({
  selector: 'app-worksheet-print',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, JobWorksheetHeaderComponent],
  templateUrl: './worksheet-print.component.html',
  styleUrl: './worksheet-print.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorksheetPrintComponent implements OnInit, OnDestroy {
  @ViewChild('worksheetDocument') private worksheetDocument?: ElementRef<HTMLElement>;

  protected readonly worksheet = signal<WorksheetDto | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);
  protected readonly isGeneratingPdf = signal(false);
  protected readonly pdfError = signal(false);
  protected readonly pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  protected readonly pdfDownloadUrl = signal<string | null>(null);
  protected readonly pdfFilename = signal('worksheet.pdf');

  private readonly route = inject(ActivatedRoute);
  private readonly worksheetService = inject(WorksheetService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    const jobId = Number(this.route.snapshot.paramMap.get('jobId'));
    const testGroupId = Number(this.route.snapshot.paramMap.get('testGroupId'));

    this.worksheetService.getJobWorksheets(jobId).subscribe({
      next: (all: WorksheetDto[]) => {
        const found = all.find((w: WorksheetDto) => w.testGroupId === testGroupId) ?? null;
        this.worksheet.set(found);
        this.notFound.set(!found);
        this.isLoading.set(false);

        if (found) {
          this.pdfFilename.set(this.createFilename(found));
          this.queuePdfGeneration();
        }
      },
      error: () => {
        this.notFound.set(true);
        this.isLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokePdfUrl();
  }

  protected regeneratePdf(): void {
    this.revokePdfUrl();
    this.pdfPreviewUrl.set(null);
    this.pdfDownloadUrl.set(null);
    this.queuePdfGeneration();
  }

  protected openPdf(): void {
    const url = this.pdfDownloadUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private queuePdfGeneration(): void {
    this.pdfError.set(false);
    this.isGeneratingPdf.set(true);

    // Wait until Angular has rendered the signal-driven worksheet before capturing it.
    requestAnimationFrame(() => requestAnimationFrame(() => void this.generatePdf()));
  }

  private async generatePdf(): Promise<void> {
    const source = this.worksheetDocument?.nativeElement;
    if (!source) {
      this.isGeneratingPdf.set(false);
      this.pdfError.set(true);
      return;
    }

    try {
      await document.fonts.ready;
      const { default: html2pdf } = await import('html2pdf.js');

      const blob = await html2pdf()
        .set({
          margin: 0.25,
          filename: this.pdfFilename(),
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            backgroundColor: '#ffffff',
            logging: false,
            scale: 2,
            useCORS: true
          },
          jsPDF: {
            unit: 'in',
            // Explicit US Letter dimensions: 8.5 × 11 inches.
            format: [8.5, 11],
            orientation: 'portrait'
          }
        })
        .from(source)
        .toPdf()
        .outputPdf('blob') as Blob;

      this.revokePdfUrl();
      const objectUrl = URL.createObjectURL(blob);
      this.pdfDownloadUrl.set(objectUrl);
      this.pdfPreviewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl));
    } catch {
      // Keep the HTML worksheet visible as a fallback and allow the user to retry.
      this.pdfError.set(true);
    } finally {
      this.isGeneratingPdf.set(false);
    }
  }

  private revokePdfUrl(): void {
    const currentUrl = this.pdfDownloadUrl();
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
    }
  }

  private createFilename(worksheet: WorksheetDto): string {
    const baseName = `${worksheet.projectName ?? 'job'}-${worksheet.title ?? 'worksheet'}`
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();

    return `${baseName || 'worksheet'}.pdf`;
  }
}
